import * as React from 'react';
import { useFormikContext } from 'formik';

/**
 * Po nieudanej probie zapisu przewija do pierwszego bledu i ustawia na nim focus.
 *
 * Musi stac WEWNATRZ drzewa Formika. W dlugich formularzach (lokalizacja, pozycja
 * menu) przycisk zapisu jest na samym dole, a bledne pole moze byc kilka ekranow
 * wyzej — bez tego klikniecie "Zapisz zmiany" wygladalo, jakby nic sie nie stalo.
 */
interface ScrollToFirstErrorProps {
  /**
   * Mapowanie sciezki bledu na realna nazwe pola w DOM, gdy sie rozjezdzaja.
   * Przyklad: schemat waliduje `addressObject.streetWithNo`, ale widocznym
   * inputem jest `name="address"`.
   */
  fieldAliases?: Record<string, string>;
}

/** Splaszcza obiekt bledow Formika do listy sciezek z kropkami. */
const flattenErrorPaths = (errors: unknown, prefix = ''): string[] => {
  if (!errors) return [];
  if (typeof errors === 'string') return prefix ? [prefix] : [];
  if (Array.isArray(errors)) {
    return errors.flatMap((entry, index) => flattenErrorPaths(entry, `${prefix}[${index}]`));
  }
  if (typeof errors === 'object') {
    return Object.entries(errors as Record<string, unknown>).flatMap(([key, value]) =>
      flattenErrorPaths(value, prefix ? `${prefix}.${key}` : key),
    );
  }
  return [];
};

/**
 * Kandydaci na selektor dla danej sciezki: najpierw dokladne trafienie, potem
 * coraz krotsze prefiksy. Dzieki temu blad `addressObject.streetWithNo` potrafi
 * trafic w pole `addressObject`, a przez alias — w `address`.
 */
const candidateNames = (path: string, aliases?: Record<string, string>): string[] => {
  const names: string[] = [];
  const segments = path.split('.');

  for (let i = segments.length; i > 0; i -= 1) {
    const candidate = segments.slice(0, i).join('.');
    names.push(candidate);
    const alias = aliases?.[candidate];
    if (alias) names.push(alias);
  }

  return names;
};

const findTopmostErrorField = (
  paths: string[],
  aliases?: Record<string, string>,
): HTMLElement | null => {
  const found: HTMLElement[] = [];

  paths.forEach((path) => {
    for (const name of candidateNames(path, aliases)) {
      const element = document.querySelector<HTMLElement>(
        `[name="${CSS.escape(name)}"]:not([type="hidden"])`,
      );
      if (element) {
        found.push(element);
        break;
      }
    }
  });

  if (!found.length) return null;

  /* Kolejnosc w obiekcie bledow idzie za schematem, nie za ukladem strony —
     dlatego "pierwszy blad" wybieramy po pozycji w dokumencie. */
  return found.reduce((topmost, element) =>
    element.getBoundingClientRect().top + window.scrollY <
    topmost.getBoundingClientRect().top + window.scrollY
      ? element
      : topmost,
  );
};

export const ScrollToFirstError: React.FC<ScrollToFirstErrorProps> = ({ fieldAliases }) => {
  const { submitCount, isValidating, errors } = useFormikContext<Record<string, unknown>>();
  /* Formik podbija submitCount i dopiero potem waliduje, wiec czekamy na koniec
     walidacji, a refem pilnujemy, zeby zadziacac raz na probe zapisu. */
  const handledSubmitCount = React.useRef(0);

  React.useEffect(() => {
    if (submitCount === 0 || isValidating) return;
    if (handledSubmitCount.current === submitCount) return;
    handledSubmitCount.current = submitCount;

    const paths = flattenErrorPaths(errors);
    if (!paths.length) return;

    const target = findTopmostErrorField(paths, fieldAliases);
    if (!target) return;

    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    /* `block: 'center'` zamiast 'start' — appbar jest position:fixed i przykrylby
       pole przewiniete do gornej krawedzi. */
    target.focus({ preventScroll: true });
  }, [submitCount, isValidating, errors, fieldAliases]);

  return null;
};

export default ScrollToFirstError;
